import React from "react";
import type { ReactNode } from "react";
import { Collapse } from "antd";
import {
  BarChartOutlined,
  FileTextOutlined,
  RollbackOutlined,
  ShopOutlined,
  ShoppingOutlined,
  SwapOutlined,
} from "@ant-design/icons";
import "../../gestion-vente/GestionVenteForm.css";

export type CommercialDocumentKind =
  | "proforma"
  | "purchase"
  | "sales-return"
  | "purchase-return";

type SummaryRow = {
  label: ReactNode;
  value: ReactNode;
  visible?: boolean;
};

const documentIcons: Record<CommercialDocumentKind, ReactNode> = {
  proforma: <FileTextOutlined aria-hidden="true" />,
  purchase: <ShoppingOutlined aria-hidden="true" />,
  "sales-return": <RollbackOutlined aria-hidden="true" />,
  "purchase-return": <SwapOutlined aria-hidden="true" />,
};

interface CommercialDocumentModalTitleProps {
  kind: CommercialDocumentKind;
  title: ReactNode;
  subtitle: ReactNode;
  contextLabel: string;
}

export const CommercialDocumentModalTitle: React.FC<
  CommercialDocumentModalTitleProps
> = ({ kind, title, subtitle, contextLabel }) => (
  <div className="sale-form-modal__title">
    <div className="sale-form-modal__title-main">
      {documentIcons[kind]}
      <div>
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
    </div>
    <span className="sale-form-modal__context" title={contextLabel}>
      <ShopOutlined aria-hidden="true" />
      <span className="sale-form-modal__context-label">{contextLabel}</span>
    </span>
  </div>
);

interface CommercialDocumentAdditionalInfoProps {
  summary: ReactNode;
  children: ReactNode;
}

export const CommercialDocumentAdditionalInfo: React.FC<
  CommercialDocumentAdditionalInfoProps
> = ({ summary, children }) => (
  <Collapse
    className="sale-form-additional"
    items={[
      {
        key: "additional-information",
        label: (
          <div className="sale-form-additional__label">
            <span>
              <FileTextOutlined aria-hidden="true" />
              <strong>Informations complémentaires</strong>
            </span>
            <small>{summary}</small>
          </div>
        ),
        children,
      },
    ]}
  />
);

interface CommercialDocumentSummaryProps {
  ariaLabel: string;
  title: ReactNode;
  countLabel: ReactNode;
  rows: SummaryRow[];
  totalLabel: ReactNode;
  totalValue: ReactNode;
  beforeTotal?: ReactNode;
  children?: ReactNode;
  actions: ReactNode;
}

export const CommercialDocumentSummary: React.FC<
  CommercialDocumentSummaryProps
> = ({
  ariaLabel,
  title,
  countLabel,
  rows,
  totalLabel,
  totalValue,
  beforeTotal,
  children,
  actions,
}) => (
  <aside className="sale-form-summary" aria-label={ariaLabel}>
    <div className="sale-form-summary__scroll">
      <div className="sale-form-summary__heading">
        <BarChartOutlined aria-hidden="true" />
        <h3>{title}</h3>
      </div>
      <div className="sale-form-summary__count">{countLabel}</div>
      <div className="sale-form-summary__breakdown">
        {rows
          .filter((row) => row.visible !== false)
          .map((row, index) => (
            <div key={index}>
              <span>{row.label}</span>
              <strong>{row.value}</strong>
            </div>
          ))}
      </div>
      {beforeTotal}
      <div className="sale-form-summary__total">
        <span>{totalLabel}</span>
        <strong>{totalValue}</strong>
      </div>
      {children}
    </div>
    <div className="sale-form-summary__actions">{actions}</div>
  </aside>
);
